import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { adminApi } from "../services/api";
import MultiSelectDropdown from "../components/common/MultiSelectDropdown";
import Table from "../components/common/Table";
import TableLoader from "../components/common/TableLoader";
import Tooltip from "../components/common/Tooltip";
import { formatPercentage } from "../utils";

const AVATAR_COLORS = ["#3a47ff", "#10b981", "#f59e0b", "#ec4899", "#8b5cf6", "#06b6d4", "#ef4444", "#0a1530"];
const avatarColor = (s) => AVATAR_COLORS[(s || "").charCodeAt(0) % AVATAR_COLORS.length];
const initials = (n) =>
  (n || "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const AuditLogsPage = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [logs, setLogs] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState(() => {
    if (location.state?.filters?.query) return location.state.filters.query;
    return searchParams.get("query") || "";
  });
  const [userFilter, setUserFilter] = useState(() => {
    if (location.state?.filters?.userFilter) return location.state.filters.userFilter;
    const param = searchParams.get("userFilter");
    return param ? param.split(",") : [];
  });
  const [statusFilter, setStatusFilter] = useState(() => {
    if (location.state?.filters?.statusFilter) return location.state.filters.statusFilter;
    const param = searchParams.get("statusFilter");
    return param ? param.split(",") : [];
  });
  const [methodFilter, setMethodFilter] = useState(() => {
    if (location.state?.filters?.methodFilter) return location.state.filters.methodFilter;
    const param = searchParams.get("methodFilter");
    return param ? param.split(",") : [];
  });
  const [moduleFilter, setModuleFilter] = useState(() => {
    if (location.state?.filters?.moduleFilter) return location.state.filters.moduleFilter;
    return searchParams.get("module") || "none";
  });
  const [realmIdFilter, setRealmIdFilter] = useState(() => {
    if (location.state?.filters?.realmIdFilter) return location.state.filters.realmIdFilter;
    const param = searchParams.get("realmIdFilter");
    return param ? param.split(",") : [];
  });
  const [pagination, setPagination] = useState(() => {
    if (location.state?.pagination) {
      return {
        page: location.state.pagination.page || 1,
        limit: location.state.pagination.limit || 10,
        total: 0,
        totalPages: 0,
      };
    }
    const pageParam = searchParams.get("page");
    const limitParam = searchParams.get("limit");
    return {
      page: pageParam ? parseInt(pageParam) : 1,
      limit: limitParam ? parseInt(limitParam) : 10,
      total: 0,
      totalPages: 0,
    };
  });
  const [filterOptions, setFilterOptions] = useState({
    methods: [],
    responseStatuses: [],
    users: [],
    connectionRealmIds: [],
  });
  const [usersPage, setUsersPage] = useState(1);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [loadingMoreUsers, setLoadingMoreUsers] = useState(false);
  const isPaginationChange = useRef(false);
  const isInitializing = useRef(true);
  const isRestoringFromNavigation = useRef(false);
  const [stats, setStats] = useState({
    totalLogs: 0,
    errorLogs: 0,
    errorRate: 0,
  });

  useEffect(() => {
    fetchFilterOptions();
    fetchStats();
    fetchUsers();
    fetchRealmIds();

    // Update URL params when returning from navigation state
    if (location.state?.filters) {
      isRestoringFromNavigation.current = true;
      const { filters, pagination: savedPagination } = location.state;

      // Update URL params to match restored state
      const params = new URLSearchParams();
      if (filters.query) params.set("query", filters.query);
      if (filters.userFilter?.length > 0) params.set("userFilter", filters.userFilter.join(","));
      if (filters.statusFilter?.length > 0) params.set("statusFilter", filters.statusFilter.join(","));
      if (filters.methodFilter?.length > 0) params.set("methodFilter", filters.methodFilter.join(","));
      if (filters.realmIdFilter?.length > 0) params.set("realmIdFilter", filters.realmIdFilter.join(","));
      if (filters.moduleFilter && filters.moduleFilter !== "none") params.set("module", filters.moduleFilter);
      // Always set pagination params when restoring from navigation, regardless of default values
      if (savedPagination?.page) params.set("page", savedPagination.page);
      if (savedPagination?.limit) params.set("limit", savedPagination.limit);
      setSearchParams(params);
      
      // Reset flag after a short delay
      setTimeout(() => {
        isRestoringFromNavigation.current = false;
      }, 100);
    }
    
    // Mark initialization as complete
    isInitializing.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect for pagination changes (non-debounced)
  useEffect(() => {
    if (isPaginationChange.current) {
      isPaginationChange.current = false;
      return;
    }
  }, [pagination.page, pagination.limit]);

  // Main data fetching effect
  useEffect(() => {
    // Skip data fetching during initialization to avoid race conditions with URL params
    if (isInitializing.current) {
      return;
    }
    
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, query, userFilter, statusFilter, methodFilter, moduleFilter, realmIdFilter]);

  const fetchFilterOptions = async () => {
    try {
      const filtersResponse = await adminApi.getAuditLogFilters();

      setFilterOptions((prev) => ({
        ...prev,
        ...filtersResponse.data,
      }));
    } catch (err) {
      console.error("Failed to load filter options", err);
    }
  };

  const fetchUsers = async (page = 1) => {
    if (page === 1) {
      setFilterOptions((prev) => ({ ...prev, users: [] }));
      setUsersPage(1);
      setHasMoreUsers(true);
    } else {
      setLoadingMoreUsers(true);
    }

    try {
      const response = await adminApi.getUsers({
        page,
        limit: 10,
      });

      const users = response.data.users.map((u) => ({
        id: u.email,
        email: u.email,
        name: u.name,
      }));

      setFilterOptions((prev) => ({
        ...prev,
        users: page === 1 ? users : [...prev.users, ...users],
      }));

      setHasMoreUsers(response.data.pagination.page < response.data.pagination.totalPages);
      setUsersPage(page);
    } catch (err) {
      console.error("Failed to load users", err);
    } finally {
      setLoadingMoreUsers(false);
    }
  };

  const handleLoadMoreUsers = () => {
    if (!loadingMoreUsers && hasMoreUsers) {
      fetchUsers(usersPage + 1);
    }
  };

  const fetchRealmIds = async () => {
    try {
      const response = await adminApi.getRealmIds();
      setFilterOptions((prev) => ({
        ...prev,
        connectionRealmIds: response.data.realmIds || [],
      }));
    } catch (err) {
      console.error("Failed to load realm ids", err);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminApi.getAuditLogStats();
      setStats(response.data.stats);
    } catch (err) {
      console.error("Failed to load stats", err);
    }
  };

  const fetchLogs = async () => {
    if (isInitialLoading) {
      setIsInitialLoading(true);
    } else {
      setIsRefetching(true);
    }
    try {
      const filters = {};
      if (query) filters.endpoint = query;
      if (userFilter.length > 0) filters.userEmail = userFilter.join(",");
      if (statusFilter.length > 0) filters.responseStatus = statusFilter.join(",");
      if (methodFilter.length > 0) filters.method = methodFilter.join(",");
      if (moduleFilter && moduleFilter !== "none") filters.entity = moduleFilter;
      if (realmIdFilter.length > 0) filters.realmId = realmIdFilter.join(",");

      const response = await adminApi.getAuditLogs({
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
      });
      setLogs(response.data.logs);
      setPagination((prev) => ({
        ...prev,
        ...response.data.pagination,
      }));
    } catch (err) {
      setError("Failed to load audit logs");
      console.error(err);
    } finally {
      setIsInitialLoading(false);
      setIsRefetching(false);
    }
  };

  const handleApplyFilters = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchLogs();
  };

  const clearFilters = () => {
    setQuery("");
    setUserFilter([]);
    setStatusFilter([]);
    setMethodFilter([]);
    setModuleFilter("none");
    setRealmIdFilter([]);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Wrapper functions to reset page when filters change
  const handleQueryChange = (value) => {
    setQuery(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleUserFilterChange = (value) => {
    setUserFilter(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleMethodFilterChange = (value) => {
    setMethodFilter(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleModuleFilterChange = (value) => {
    setModuleFilter(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleRealmIdFilterChange = (value) => {
    setRealmIdFilter(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const updateURLParams = () => {
    // Skip URL update when restoring from navigation state to avoid overwriting
    if (isRestoringFromNavigation.current) {
      return;
    }
    
    const params = new URLSearchParams();
    if (query) params.set("query", query);
    if (userFilter.length > 0) params.set("userFilter", userFilter.join(","));
    if (statusFilter.length > 0) params.set("statusFilter", statusFilter.join(","));
    if (methodFilter.length > 0) params.set("methodFilter", methodFilter.join(","));
    if (moduleFilter !== "none") params.set("module", moduleFilter);
    if (realmIdFilter.length > 0) params.set("realmIdFilter", realmIdFilter.join(","));
    if (pagination.page !== 1) params.set("page", pagination.page);
    if (pagination.limit !== 10) params.set("limit", pagination.limit);

    setSearchParams(params);
  };

  useEffect(() => {
    updateURLParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, userFilter, statusFilter, methodFilter, moduleFilter, realmIdFilter, pagination.page, pagination.limit]);

  const getMethodColor = (method) => {
    const colors = {
      GET: "bg-blue-100 text-blue-700",
      POST: "bg-green-100 text-green-800",
      PUT: "bg-yellow-100 text-yellow-800",
      PATCH: "bg-purple-100 text-purple-800",
      DELETE: "bg-red-100 text-red-800",
    };
    return colors[method] || "bg-gray-100 text-gray-800";
  };

  const getStatusColor = (status) => {
    if (status === "success") return "bg-green-100 text-green-800";
    if (status === "error") return "bg-red-100 text-red-800";
    return "bg-yellow-100 text-yellow-800";
  };

  const handlePageChange = useCallback((newPage) => {
    isPaginationChange.current = true;
    setPagination((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handlePageSizeChange = useCallback((newPageSize) => {
    isPaginationChange.current = true;
    setPagination((prev) => ({ ...prev, limit: newPageSize, page: 1 }));
  }, []);

  const columns = [
    {
      key: "user",
      label: "User",
      render: (l) =>
        l.userEmail ? (
          <div className="flex items-center gap-2">
            <div
              className="w-7 h-7 rounded-full grid place-items-center text-white font-semibold text-[10px] shrink-0"
              style={{ background: avatarColor(l.userEmail) }}
            >
              {initials(l.userEmail)}
            </div>
            <div className="text-xs truncate">{l.userEmail}</div>
          </div>
        ) : (
          <span className="text-muted text-xs">—</span>
        ),
    },
    {
      key: "endpoint",
      label: "API Endpoint",
      render: (l) => (
        <div className="flex items-center gap-2" title={l.endpoint + (l.queryParams ? `?${new URLSearchParams(l.queryParams).toString()}` : "")}>
          <span className={`text-[10px] font-bold py-0.5 px-1.5 rounded tracking-wide ${getMethodColor(l.method)}`}>{l.method}</span>
          <span className="font-mono text-[11px] text-ink truncate">{l.endpoint}</span>
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (l) => (
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-medium">{l.status}</span>
          <Tooltip
            content={l.responseStatus === "error" ? l.errorMessage || "Error occurred" : l.successMessage || "Request successful"}
            position="top"
          >
            <span className={`inline-flex items-center gap-1 py-0.5 px-2 rounded-full text-[11px] font-semibold ${getStatusColor(l.responseStatus)}`}>
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  l.responseStatus === "success" ? "bg-green-600" : l.responseStatus === "error" ? "bg-red-600" : "bg-yellow-600"
                }`}
              ></span>
              {l.responseStatus}
            </span>
          </Tooltip>
        </div>
      ),
    },
    {
      key: "realmId",
      label: "Realm ID",
      render: (l) => <span className="font-mono text-[11px] text-muted truncate">{l.realmId || "—"}</span>,
    },
    {
      key: "retrievalCounts",
      label: "Retrieval Counts",
      className: "text-center",
      cellClassName: "text-center",
      render: (l) => (
        <span className="font-mono text-[11px] text-muted">{l.responseBody?.data?.counts ?? "—"}</span>
      ),
    },
    {
      key: "timestamp",
      label: "Timestamp",
      render: (l) => <span className="font-mono text-[11px] text-muted whitespace-nowrap">{new Date(l.timestamp).toLocaleString()}</span>,
    },
    {
      key: "duration",
      label: "Duration",
      render: (l) => <span className="font-mono text-[11px] text-muted">{l.duration ? `${(l.duration / 1000).toFixed(2)}s` : "—"}</span>,
    },
  ];

  return (
    <div className="px-6 py-8 pb-24 font-sans text-ink bg-white h-full">
      <div className="mb-8">
        <h1 className="text-[28px] font-bold tracking-tight m-0">Audit Logs</h1>
        <p className="text-muted text-[15px] mt-2 mb-0">Track user actions, API calls, and response statuses across your QuickBooks integrations.</p>
      </div>

      <div className="grid grid-cols-5 gap-4 mb-6">
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Total events</div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight">{stats.totalLogs}</div>
        </div>
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Success</div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight text-green-700">{stats.totalLogs - stats.errorLogs}</div>
        </div>
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Failed</div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight text-red-700">{stats.errorLogs}</div>
        </div>
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Error Rate</div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight text-yellow-700">{formatPercentage(stats?.errorRate)}%</div>
        </div>
        <div className="border border-line rounded-xl p-5 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Success Rate</div>
          <div className="text-[26px] font-bold mt-1.5 tracking-tight text-green-700">
            {stats?.errorRate && formatPercentage(100 - stats?.errorRate)}%
          </div>
        </div>
      </div>

      <div className="mb-5">
        <div className="flex gap-3 mb-3 items-center flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input
              placeholder="Search endpoint…"
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              className="w-full py-2.5 px-3 pl-9 border border-line rounded-lg text-[13.5px] bg-white transition-all focus:outline-none focus:border-indigo focus:ring-3 focus:ring-indigo/10"
            />
          </div>

          <MultiSelectDropdown
            options={filterOptions.users.map((u) => ({ label: u.email || u.name, value: u.id }))}
            value={userFilter}
            onSelect={handleUserFilterChange}
            buttonLabel="Users"
            searchable={true}
            onScrollEnd={handleLoadMoreUsers}
            isLoadingMore={loadingMoreUsers}
            hasMore={hasMoreUsers}
          />

          <MultiSelectDropdown
            options={[
              { label: "Success", value: "success" },
              { label: "Error", value: "error" },
              { label: "Redirect", value: "redirect" },
            ]}
            value={statusFilter}
            onSelect={handleStatusFilterChange}
            buttonLabel="Status"
            searchable={false}
          />

          <MultiSelectDropdown
            options={[
              { label: "GET", value: "GET" },
              { label: "POST", value: "POST" },
              { label: "PUT", value: "PUT" },
              { label: "PATCH", value: "PATCH" },
              { label: "DELETE", value: "DELETE" },
            ]}
            value={methodFilter}
            onSelect={handleMethodFilterChange}
            buttonLabel="Method"
            searchable={false}
            renderBadge={(option) => (
              <span
                className={`font-bold ${
                  option.value === "GET"
                    ? "text-blue-700"
                    : option.value === "POST"
                      ? "text-green-800"
                      : option.value === "PUT"
                        ? "text-yellow-800"
                        : option.value === "PATCH"
                          ? "text-purple-800"
                          : option.value === "DELETE"
                            ? "text-red-800"
                            : "text-ink"
                }`}
              >
                {option.label}
              </span>
            )}
          />

          <MultiSelectDropdown
            options={filterOptions.connectionRealmIds.map((realmId) => ({ label: realmId, value: realmId }))}
            value={realmIdFilter}
            onSelect={handleRealmIdFilterChange}
            buttonLabel="Realm ID"
            searchable={true}
          />

          <div className="relative min-w-[160px]">
            <select
              value={moduleFilter}
              onChange={(e) => handleModuleFilterChange(e.target.value)}
              className="w-full py-2.5 px-3.5 pr-10 border border-line rounded-lg bg-white cursor-pointer text-[13px] font-medium text-ink hover:bg-soft transition-all appearance-none focus:outline-none focus:border-indigo focus:ring-3 focus:ring-indigo/10"
            >
              <option value="none">Group By: None</option>
              <option value="Account">Group By: Account</option>
              <option value="Accounting">Group By: Accounting</option>
              <option value="Attachment">Group By: Attachment</option>
              <option value="Auth">Group By: Auth</option>
              <option value="Bank-Account">Group By: Bank-Account</option>
              <option value="Bill">Group By: Bill</option>
              <option value="Chart-of-Accounts">Group By: Chart-of-Accounts</option>
              <option value="Customer">Group By: Customer</option>
              <option value="Inventory">Group By: Inventory</option>
              <option value="Invoice">Group By: Invoice</option>
              <option value="Payment">Group By: Payment</option>
              <option value="Quickbooks">Group By: Quickbooks</option>
              <option value="Report">Group By: Report</option>
              <option value="Smart-Invoice">Group By: Smart-Invoice</option>
              <option value="Vendor">Group By: Vendor</option>
            </select>
            <svg
              className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </div>

          <button
            onClick={clearFilters}
            disabled={
              userFilter.length === 0 &&
              methodFilter.length === 0 &&
              statusFilter.length === 0 &&
              realmIdFilter.length === 0 &&
              query === "" &&
              moduleFilter === "none"
            }
            className="py-2.5 px-3.5 border border-line rounded-lg bg-[#1e293b] cursor-pointer text-[13px] font-medium text-white hover:text-white hover:bg-[#334155] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="relative">
        <Table
          columns={columns}
          data={logs}
          loading={isInitialLoading}
          error={error}
          pagination={pagination}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          maxHeight="calc(100vh - 400px)"
          emptyState={
            <tr>
              <td colSpan={7} className="py-20 px-4">
                <div className="flex flex-col items-center justify-center text-center">
                  <svg
                    width="64"
                    height="64"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#9ca3af"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mb-4"
                  >
                    <path d="14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="12" y1="18" x2="12" y2="12"></line>
                    <line x1="9" y1="15" x2="15" y2="15"></line>
                  </svg>
                  <h3 className="text-lg font-semibold text-ink mb-2">No Audit Logs Found</h3>
                  <p className="text-sm text-muted max-w-md">
                    {userFilter.length > 0 || statusFilter.length > 0 || methodFilter.length > 0 || realmIdFilter.length > 0 || moduleFilter !== "none" || query
                      ? "No logs match your current filters. Try adjusting your search criteria."
                      : "No audit logs have been recorded yet. Logs will appear here once API activity begins."}
                  </p>
                </div>
              </td>
            </tr>
          }
        />
        {isRefetching && <TableLoader overlay={true} message="Loading..." />}
      </div>
    </div>
  );
};

export default AuditLogsPage;
